import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';
import {
  errorClass,
  fieldBaseClass,
  fieldBorderClass,
  hintClass,
  labelClass,
} from '@/shared/ui/core/styles';
import { useFormFieldContext } from '@/shared/ui/core/formFieldContext';
import type { FieldTone } from '@/shared/ui/core/types';

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hint?: string;
  error?: string;
  tone?: FieldTone;
  required?: boolean;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { className, label, hint, error, tone, id, rows = 4, ...props },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const formField = useFormFieldContext();
  const effectiveError = error || (formField.inFormField ? formField.error : undefined);
  const describedBy = effectiveError ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;
  const resolvedTone: FieldTone = tone ?? (effectiveError ? 'error' : 'default');
  const shouldRenderError = Boolean(effectiveError && (!formField.inFormField || (!formField.error && Boolean(error))));

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label ? (
        <label htmlFor={fieldId} className={cn(labelClass, 'flex items-center gap-1')}>
          <span>{label}</span>
          {props.required ? (
            <span className="text-danger font-bold text-sm leading-none" aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        aria-invalid={resolvedTone === 'error' || undefined}
        aria-describedby={describedBy}
        className={cn(
          fieldBaseClass,
          fieldBorderClass[resolvedTone],
          'min-h-24 resize-y px-3 py-2.5',
          className,
        )}
        {...props}
      />
      {shouldRenderError ? (
        <p id={`${fieldId}-error`} className={errorClass} role="alert">
          {effectiveError}
        </p>
      ) : hint ? (
        <p id={`${fieldId}-hint`} className={hintClass}>
          {hint}
        </p>
      ) : null}
    </div>
  );
});
